import React, { useEffect, useRef, useState, useCallback } from "react";
import { useGoogleMaps } from "../../../../hooks";
import {
  TerraDraw,
  TerraDrawPolygonMode,
  TerraDrawSelectMode,
  TerraDrawRenderMode,
} from "terra-draw";
import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter";

const loadScriptIfNeeded = (apiKey, onReady) => {
  if (window.google && window.google.maps) {
    onReady();
    return;
  }
  if (!apiKey) return;
  if (document.getElementById("__googleMapsScriptZones")) return;
  const js = document.createElement("script");
  js.id = "__googleMapsScriptZones";
  js.async = true;
  js.defer = true;
  js.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry`;
  js.onload = onReady;
  document.body.appendChild(js);
};

const polygonGeoJsonToData = (feature) => {
  if (!feature?.geometry?.coordinates) return null;
  const coords = feature.geometry.coordinates[0];
  if (!coords || !Array.isArray(coords) || coords.length < 3) return null;

  return coords.slice(0, -1).map((coord) => ({
    lat: coord[1],
    lng: coord[0],
  }));
};

const getCoordinatesFromValue = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) return value;
  if (value.coordinates && Array.isArray(value.coordinates))
    return value.coordinates;
  return null;
};

const BusinessZonePolygonMap = ({ apiKey, center, value, onChange }) => {
  const { isLoaded } = useGoogleMaps();
  const ref = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef(null);
  const terraDrawRef = useRef(null);
  const polygonRef = useRef(null);
  const mapReadyRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const initializedRef = useRef(false);
  const [pointCount, setPointCount] = useState(0);
  const [hasDrawing, setHasDrawing] = useState(false);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!isLoaded) {
      loadScriptIfNeeded(apiKey, () => setMapReady(true));
    } else {
      setMapReady(true);
    }
  }, [isLoaded, apiKey]);

  const getPolygonData = useCallback((polygon) => {
    const data = [];
    for (const pos of polygon.getPath().getArray()) {
      data.push({ lat: pos.lat(), lng: pos.lng() });
    }
    return data;
  }, []);

  const handlePolygonPathChanged = useCallback(() => {
    if (!polygonRef.current) return;
    const data = getPolygonData(polygonRef.current);
    if (onChangeRef.current) {
      onChangeRef.current(data);
      setPointCount(data.length);
    }
  }, [getPolygonData]);

  const initializeTerraDraw = useCallback(
    (map, initialValue) => {
      if (initializedRef.current) return;
      initializedRef.current = true;

      const initialCoordinates = getCoordinatesFromValue(initialValue);

      // If we have existing data, use native Google Maps Polygon (editable)
      if (initialCoordinates && initialCoordinates.length > 0) {
        const paths = initialCoordinates.map((coord) => ({
          lat: coord.lat || coord[1],
          lng: coord.lng || coord[0],
        }));

        const polygon = new window.google.maps.Polygon({
          paths,
          fillColor: "#2C7BE5",
          fillOpacity: 0.2,
          strokeColor: "#03459E",
          strokeWeight: 2,
          editable: true,
          draggable: false,
          map,
        });

        polygonRef.current = polygon;
        setPointCount(initialCoordinates.length);
        setHasDrawing(true);

        // Listen for path changes
        window.google.maps.event.addListener(
          polygon.getPath(),
          "set_at",
          handlePolygonPathChanged,
        );
        window.google.maps.event.addListener(
          polygon.getPath(),
          "insert_at",
          handlePolygonPathChanged,
        );
        window.google.maps.event.addListener(
          polygon.getPath(),
          "remove_at",
          handlePolygonPathChanged,
        );

        // Fit bounds to polygon
        const bounds = new window.google.maps.LatLngBounds();
        paths.forEach((coord) => bounds.extend(coord));
        map.fitBounds(bounds);

        return;
      }

      if (terraDrawRef.current) {
        try {
          terraDrawRef.current.stop();
        } catch {
          // Ignore errors when stopping TerraDraw
        }
        terraDrawRef.current = null;
      }

      const terraDraw = new TerraDraw({
        adapter: new TerraDrawGoogleMapsAdapter({
          map,
          lib: window.google.maps,
        }),
        modes: [
          new TerraDrawPolygonMode({
            pointerDistance: 10,
            styles: {
              fillColor: "#2C7BE5",
              fillOpacity: 0.2,
              outlineColor: "#03459E",
              outlineWidth: 2,
            },
          }),
          new TerraDrawSelectMode({
            flags: {
              polygon: {
                feature: {
                  draggable: false,
                  coordinates: {
                    midpoints: true,
                    draggable: true,
                    deletable: true,
                  },
                },
              },
            },
            styles: {
              selectedPolygonColor: "#2C7BE5",
              selectedPolygonFillOpacity: 0.2,
              selectedPolygonOutlineColor: "#03459E",
              selectedPolygonOutlineWidth: 2,
            },
          }),
          new TerraDrawRenderMode({ modeName: "static" }),
        ],
      });

      terraDraw.start();
      terraDrawRef.current = terraDraw;

      terraDraw.on("finish", (id) => {
        const snapshot = terraDraw.getSnapshot();
        const feature = snapshot.find((f) => f.id === id);
        if (feature && feature.geometry.type === "Polygon") {
          const coordinates = polygonGeoJsonToData(feature);
          if (coordinates && onChangeRef.current) {
            onChangeRef.current(coordinates);
            setPointCount(coordinates.length);
          }
          terraDraw.setMode("select");
          terraDraw.selectFeature(id);
          setHasDrawing(true);
        }
      });

      terraDraw.on("change", (ids, changeType) => {
        if (changeType === "update") {
          const snapshot = terraDraw.getSnapshot();
          ids.forEach((id) => {
            const feature = snapshot.find((f) => f.id === id);
            if (feature && feature.geometry.type === "Polygon") {
              const coordinates = polygonGeoJsonToData(feature);
              if (coordinates && onChangeRef.current) {
                onChangeRef.current(coordinates);
                setPointCount(coordinates.length);
              }
            }
          });
        }
      });

      terraDraw.setMode("polygon");
    },
    [center, handlePolygonPathChanged],
  );

  const handleClear = useCallback(() => {
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }

    if (terraDrawRef.current) {
      try {
        terraDrawRef.current.clear();
        terraDrawRef.current.setMode("polygon");
      } catch {
        // Ignore errors when clearing TerraDraw
      }
    } else if (mapRef.current && window.google?.maps) {
      initializedRef.current = false;
      initializeTerraDraw(mapRef.current, null);
    }

    setPointCount(0);
    setHasDrawing(false);
    if (onChangeRef.current) {
      onChangeRef.current(null);
    }
  }, [initializeTerraDraw]);

  useEffect(() => {
    if (
      !mapReady ||
      !window.google?.maps ||
      !ref.current ||
      !center?.lat ||
      !center?.lng
    )
      return;
    if (mapReadyRef.current) return;

    const map = new window.google.maps.Map(ref.current, {
      center: { lat: center.lat, lng: center.lng },
      zoom: 12,
      streetViewControl: false,
      mapTypeControl: false,
    });
    mapRef.current = map;
    mapReadyRef.current = true;

    window.google.maps.event.addListenerOnce(map, "idle", () => {
      initializeTerraDraw(map, value);
    });

    return () => {
      if (terraDrawRef.current) {
        try {
          terraDrawRef.current.stop();
        } catch {
          // Ignore errors when stopping TerraDraw
        }
        terraDrawRef.current = null;
      }
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
      mapReadyRef.current = false;
      initializedRef.current = false;
    };
  }, [mapReady, center?.lat, center?.lng]);

  useEffect(() => {
    if (
      mapRef.current &&
      mapReadyRef.current &&
      !initializedRef.current &&
      value
    ) {
      initializeTerraDraw(mapRef.current, value);
    }
  }, [value, initializeTerraDraw]);

  return (
    <div className="space-y-2">
      <div
        className="relative h-64 w-full overflow-hidden rounded-lg border border-gray-700 bg-gray-800 sm:h-80"
        ref={ref}
      />
      <div className="flex items-center justify-between">
        {pointCount > 0 ? (
          <div className="text-xs text-gray-400">
            Polygon has {pointCount} points. Click and drag points to edit.
          </div>
        ) : (
          <div className="text-xs text-gray-400">
            Click on the map to start drawing a polygon.
          </div>
        )}
        {hasDrawing && (
          <button
            type="button"
            onClick={handleClear}
            className="rounded border border-red-600/30 bg-red-600/20 px-3 py-1 text-xs text-red-400 transition-colors hover:bg-red-600/30 hover:text-red-300"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
};

export default BusinessZonePolygonMap;
